import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { Send, MapPin, Mail, Linkedin, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
// --- Constants & Types ---
const COLORS = {
  background: '#0D2B24',
  soilOverlay: 'rgba(10, 17, 17, 0.4)',
  formContainer: '#1F3A33',
  teal: '#2BC7B7',
  glowTeal: '#4DE7D4',
  error: '#A38E78',
  white: '#F3F5F1' // Mycelium White
};
type FormState = {
  name: string;
  email: string;
  message: string;
};
type FieldStatus = 'empty' | 'typing' | 'valid' | 'error';
// --- Helper Functions for Organic Paths ---
// Generate a random-ish but deterministic wiggle path
const generateWiggle = (startX: number, startY: number, length: number, seed: number, amplitude: number = 10) => {
  let d = `M ${startX} ${startY}`;
  const segments = Math.floor(length / 20);
  let currentY = startY;
  let currentX = startX;
  for (let i = 0; i < segments; i++) {
    const nextY = currentY + 20;
    // Pseudo-random offset based on seed and index
    const offset = Math.sin(seed + i) * amplitude;
    const nextX = startX + offset;
    // Control points for smooth bezier
    const cp1x = currentX;
    const cp1y = currentY + 10;
    const cp2x = nextX;
    const cp2y = nextY - 10;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${nextX} ${nextY}`;
    currentY = nextY;
    currentX = nextX;
  }
  // Final segment to exact length if needed, or just stop
  return d;
};
// Generate branches
const generateBranches = (parentPath: string, count: number, seed: number) => {
  // This is a simplified approach. In a real complex system we'd parse the SVG path.
  // Here we'll just spawn branches from estimated points along the vertical descent.
  // Assuming the parent path goes roughly down from startY.
  const branches = [];
  for (let i = 0; i < count; i++) {
    const verticalOffset = 30 + i * 40; // Spacing
    const side = i % 2 === 0 ? 1 : -1;
    const branchSeed = seed + i * 100;
    // Start point (rough estimate relative to parent flow)
    // We'll just make them start from the center line + wiggle offset
    const startX = Math.sin(branchSeed) * 10;
    const startY = verticalOffset;
    const length = 20 + Math.sin(branchSeed * 2) * 15; // Random length 20-35
    const endX = startX + length * side * 0.8;
    const endY = startY + length * 0.6;
    // Quadratic curve for branch
    const d = `M ${startX} ${startY} Q ${startX + side * 5} ${startY + length / 2}, ${endX} ${endY}`;
    branches.push({
      d,
      id: `branch-${seed}-${i}`,
      yOffset: verticalOffset
    });
  }
  return branches;
};
// --- Components ---
const FounderCard = () => <motion.div initial={{
  opacity: 0,
  x: 20
}} animate={{
  opacity: 1,
  x: 0
}} transition={{
  delay: 0.8
}} className="bg-[#1F3A33] rounded-2xl p-6 border border-[#2BC7B7]/20 shadow-lg max-w-xs w-full" data-id="element-426" data-mp="9ce4wtg76d7y">
    <div className="flex items-center gap-4 mb-4" data-id="element-427" data-mp="23laxfyitfxjw">
      <div className="relative" data-id="element-428" data-mp="23mue3q8dwam7">
        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#2BC7B7] relative z-10" data-id="element-429" data-mp="23n47lnsn0zwn">
          <img src="https://cdn.magicpatterns.com/uploads/nPrbWRd99BLA7hL4gLSpQo/7_Natalya.jpg" alt="Natalya Wallin" className="w-full h-full object-cover" data-id="element-430" data-mp="1tvhmmzqhzmgp" />
        </div>
        {/* Ring pulse */}
        <motion.div className="absolute inset-0 rounded-full border border-[#2BC7B7]" animate={{
        scale: [1, 1.2, 1],
        opacity: [0.5, 0, 0.5]
      }} transition={{
        duration: 3,
        repeat: Infinity
      }} data-id="element-431" data-mp="2mj415319kbic" />
      </div>
      <div data-id="element-432" data-mp="27m39t0mziike">
        <h3 className="text-white font-semibold text-lg" data-id="element-433" data-mp="27mmqke0wf0wu">Natalya Wallin</h3>
        <p className="text-[#2BC7B7] text-xs uppercase tracking-wider" data-id="element-434" data-mp="117lrw0kho4mk">
          Founding Partner & GP
        </p>
      </div>
    </div>
    <p className="text-[#F3F5F1]/70 text-sm italic border-l-2 border-[#2BC7B7]/30 pl-3" data-id="element-435" data-mp="w9u2bd7bgulj">
      "I personally read every message. We're looking for the outliers, the
      resilient, the ones forged in nature."
    </p>
  </motion.div>;
const ContactCard = ({
  icon: Icon,
  title,
  value,
  href
}: any) => <a href={href} target={href ? '_blank' : undefined} rel={href ? 'noopener noreferrer' : undefined} className={`flex flex-col items-center p-6 bg-[#1F3A33] rounded-xl border border-[#2BC7B7]/30 hover:border-[#2BC7B7]/80 hover:bg-[#2BC7B7]/15 hover:shadow-[0_0_20px_rgba(43,199,183,0.2)] transition-all duration-300 group ${!href ? 'cursor-default' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]'}`} data-id="element-436" data-mp="2b1mogeridn9n">
    <div className="w-10 h-10 rounded-full bg-[#0D2B24] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform" data-id="element-437" data-mp="2yssc3g3oxjui">
      <Icon className="w-6 h-6 text-[#2BC7B7] group-hover:text-[#4DE7D4] transition-colors" data-id="element-438" data-mp="1idie3fxzsm0m" />
    </div>
    <span className="text-[#F3F5F1]/60 text-xs uppercase tracking-wider mb-1 font-medium" data-id="element-439" data-mp="1nfpv4jxois9t">
      {title}
    </span>
    <span className="text-white font-medium text-center" data-id="element-440" data-mp="5imitsl2kmne">{value}</span>
  </a>;
export function ContactRootSystem() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    message: ''
  });
  const [focused, setFocused] = useState<keyof FormState | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success'>('idle');
  // Validation Logic
  const isNameValid = form.name.length > 2;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const isMessageValid = form.message.length > 10;
  const allValid = isNameValid && isEmailValid && isMessageValid;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allValid) return;
    setStatus('sending');
    // Simulate network request and bloom animation time
    await new Promise(resolve => setTimeout(resolve, 3500));
    setStatus('success');
  };
  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      message: ''
    });
    setStatus('idle');
  };
  // Calculate root lengths based on input
  const nameRootLength = Math.min(form.name.length * 5, 150);
  const emailRootLength = Math.min(form.email.length * 4, 150);
  const messageRootLength = Math.min(form.message.length * 1.5, 220);
  return <div className="min-h-screen bg-[#0D2B24] relative overflow-hidden font-sans selection:bg-[#2BC7B7]/30" data-id="element-441" data-mp="1csslfxv5zqhv">
      {/* Background Textures */}
      <div className="absolute inset-0 bg-[#0A1111] opacity-40 pointer-events-none" data-id="element-442" data-mp="f8pw2227mjl3" />
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
    }} data-id="element-443" data-mp="k7iy8yc1qo5y" />

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-444" data-mp="3q0a9r8amd4ds">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-445" data-mp="3l2ijeipmhqbp">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight" data-id="element-446" data-mp="3g9yxmygs7ovt">
            LET'S GROW SOMETHING TOGETHER
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-[#2BC7B7] text-lg font-medium mb-6" data-id="element-447" data-mp="32dlpify50zu0">
            Plant the seed of a conversation
          </motion.p>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-[#F3F5F1]/70 leading-relaxed" data-id="element-448" data-mp="qjvn82vnyllz">
            If you are passionate about building a world of vitality as an
            entrepreneur, investor, or partner, we want to hear from you.
            <br data-id="element-449" data-mp="3ctxn1wq81o5i" />
            <br data-id="element-450" data-mp="3cxlij1f88kmv" />
            Vitality Vida Partners, LLC
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-id="element-451" data-mp="3hs8w458ozumo">
          {/* Left Column: Form (Centerpiece) */}
          <div className="lg:col-span-8 lg:col-start-3 xl:col-span-6 xl:col-start-4 relative" data-id="element-452" data-mp="37vw4re0mdsv3">
            {/* Form Container */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6
          }} className="bg-[#1F3A33] rounded-[24px] p-8 md:p-12 relative overflow-hidden shadow-2xl border border-[#2BC7B7]/20" style={{
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.5)'
          }} data-id="element-453" data-mp="116rd09cv6p1p">
              {/* Soil Texture Overlay inside form */}
              <div className="absolute inset-0 bg-[#0A1111] opacity-30 pointer-events-none mix-blend-multiply" data-id="element-454" data-mp="39c9zwfg53g3b" />
              <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%232BC7B7' fill-opacity='0.4'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' /%3E%3C/g%3E%3C/svg%3E")`
            }} data-id="element-455" data-mp="35ay8c4dpgksh" />

              {/* SUCCESS STATE OVERLAY */}
              <AnimatePresence data-id="element-456" data-mp="nuwz6w60h2a5">
                {status === 'success' && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#0D2B24]/90 backdrop-blur-sm text-center p-8" data-id="element-457" data-mp="stgg2i5qznvn">
                    <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  duration: 1.5
                }} className="w-24 h-24 rounded-full bg-[#2BC7B7]/20 flex items-center justify-center mb-6 relative" data-id="element-458" data-mp="16r54u12xrf5z">
                      <div className="absolute inset-0 rounded-full border border-[#2BC7B7] animate-ping opacity-20" data-id="element-459" data-mp="1ugpo6l304efs" />
                      <Check className="w-10 h-10 text-[#2BC7B7]" data-id="element-460" data-mp="3lmvmnli6ott7" />
                    </motion.div>
                    <motion.h2 initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.2
                }} className="text-3xl font-bold text-[#2BC7B7] mb-2" data-id="element-461" data-mp="46dps5azl5w3">
                      Message Sent
                    </motion.h2>
                    <motion.p initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.3
                }} className="text-[#F3F5F1]/80 mb-8" data-id="element-462" data-mp="i76n0xsoaf2w">
                      Thank you for reaching out. We'll be in touch soon.
                    </motion.p>
                    <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.4
                }} data-id="element-463" data-mp="uor7t674gwca">
                      <Button variant="outline" onClick={resetForm} data-id="element-464" data-mp="bpwt1x7nrvw4">
                        Send Another
                      </Button>
                    </motion.div>
                  </motion.div>}
              </AnimatePresence>

              {/* ROOT VISUALIZATION LAYER (Behind inputs) */}
              <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden" data-id="element-465" data-mp="61emusgdfj0p">
                <svg className="w-full h-full" preserveAspectRatio="none" data-id="element-466" data-mp="2033h4o3bu0y">
                  <defs data-id="element-467" data-mp="1xyrdzg1fopl">
                    <filter id="glow" x="-20%" y="-20%" width="140%" height="140%" data-id="element-468" data-mp="3t648owldjteo">
                      <feGaussianBlur stdDeviation="4" result="blur" data-id="element-469" data-mp="3j8q1zop96izk" />
                      <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-470" data-mp="3ehhw32n4tfr5" />
                    </filter>
                    <linearGradient id="rootGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-471" data-mp="35gs5qegn4hkt">
                      <stop offset="0%" stopColor="#2BC7B7" data-id="element-472" data-mp="2vi3y89ma7kvd" />
                      <stop offset="100%" stopColor="#1F3A33" stopOpacity="0" data-id="element-473" data-mp="2vkp02ymky0t4" />
                    </linearGradient>
                  </defs>

                  {/* Name Root */}
                  <g transform="translate(100, 100)" data-id="element-474" data-mp="2qc77fpiozvo5">
                    {' '}
                    {/* Approximate position below Name input */}
                    {/* We need dynamic positioning. Since we can't easily get exact DOM rects in this static SVG setup without refs,
                     we'll use percentage based positioning that aligns with the inputs.
                     Name Input is roughly at top 15%. Email at 40%. Message at 65%.
                     */}
                  </g>
                </svg>

                {/* Dynamic SVG Layer using absolute positioning relative to container */}
                {/* Name Root */}
                <div className="absolute top-[100px] left-1/2 -translate-x-1/2 w-full h-[500px] pointer-events-none" data-id="element-475" data-mp="1jxy36jk58r2k">
                  <svg className="w-full h-full overflow-visible" data-id="element-476" data-mp="1ovznt8x7wyg5">
                    <RootSystem isActive={form.name.length > 0} isValid={isNameValid} length={nameRootLength} seed={1} color={isNameValid ? COLORS.glowTeal : COLORS.teal} opacity={isNameValid ? 1 : 0.6} data-id="element-477" data-mp="1gw3fgeqptm99" />
                  </svg>
                </div>

                {/* Email Root */}
                <div className="absolute top-[200px] left-1/2 -translate-x-1/2 w-full h-[400px] pointer-events-none" data-id="element-478" data-mp="xyaaz80g149r">
                  <svg className="w-full h-full overflow-visible" data-id="element-479" data-mp="szp9l59ggejq">
                    <RootSystem isActive={form.email.length > 0} isValid={isEmailValid} length={emailRootLength} seed={2} color={isEmailValid ? COLORS.glowTeal : COLORS.teal} opacity={isEmailValid ? 1 : 0.6} data-id="element-480" data-mp="17vy987x9ctwo" />
                  </svg>
                </div>

                {/* Message Root */}
                <div className="absolute top-[380px] left-1/2 -translate-x-1/2 w-full h-[250px] pointer-events-none" data-id="element-481" data-mp="2ltox0r91woet">
                  <svg className="w-full h-full overflow-visible" data-id="element-482" data-mp="2qrqfb9ynkjp8">
                    <RootSystem isActive={form.message.length > 0} isValid={isMessageValid} length={messageRootLength} seed={3} color={isMessageValid ? COLORS.glowTeal : COLORS.teal} opacity={isMessageValid ? 1 : 0.6} data-id="element-483" data-mp="2cvwele8190n7" />
                  </svg>
                </div>

                {/* Convergence Zone Bloom (Only when all valid) */}
                <div className="absolute bottom-0 left-0 w-full h-[200px] flex items-end justify-center pb-20" data-id="element-484" data-mp="2ztggv8m90z0q">
                  <AnimatePresence data-id="element-485" data-mp="3nisow55ub11g">
                    {allValid && <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="absolute bottom-[130px]" data-id="element-486" data-mp="3sbc416k6e48d">
                        <div className="w-4 h-4 rounded-full bg-[#4DE7D4] shadow-[0_0_20px_#4DE7D4] animate-pulse" data-id="element-487" data-mp="2owuozp34hmtf" />
                        {/* Connecting tendrils */}
                        <svg className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[300px] h-[200px] overflow-visible" style={{
                      transform: 'translateX(-50%)'
                    }} data-id="element-488" data-mp="3hjnxwuqo1sei">
                          <motion.path d="M 0 0 Q -50 -50, -100 -150" stroke="#4DE7D4" strokeWidth="1" fill="none" initial={{
                        pathLength: 0
                      }} animate={{
                        pathLength: 1
                      }} transition={{
                        duration: 1
                      }} data-id="element-489" data-mp="216gxq0sd3rzw" />
                          <motion.path d="M 0 0 Q 50 -50, 100 -150" stroke="#4DE7D4" strokeWidth="1" fill="none" initial={{
                        pathLength: 0
                      }} animate={{
                        pathLength: 1
                      }} transition={{
                        duration: 1
                      }} data-id="element-490" data-mp="vdcc7rq23k3z" />
                          <motion.path d="M 0 0 L 0 -100" stroke="#4DE7D4" strokeWidth="1" fill="none" initial={{
                        pathLength: 0
                      }} animate={{
                        pathLength: 1
                      }} transition={{
                        duration: 1
                      }} data-id="element-491" data-mp="1j34mdan7lhyj" />
                        </svg>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Bloom Animation on Success */}
                  <AnimatePresence data-id="element-492" data-mp="2fqj2t7sv5ewk">
                    {status === 'success' && <motion.div className="absolute bottom-[130px] z-10" data-id="element-493" data-mp="hf2745n33zvq">
                        {/* Radial explosion */}
                        {[...Array(12)].map((_, i) => <motion.div key={i} className="absolute top-0 left-0 w-1 h-40 bg-gradient-to-t from-transparent to-[#4DE7D4]" style={{
                      transformOrigin: 'bottom center',
                      rotate: i * 30
                    }} initial={{
                      scaleY: 0,
                      opacity: 0
                    }} animate={{
                      scaleY: 1,
                      opacity: [0, 1, 0]
                    }} transition={{
                      duration: 1.5,
                      delay: 0.5
                    }} data-id="element-494" data-mp="dwwer28d439h" data-mpi={i} />)}
                        <motion.div className="w-[600px] h-[600px] rounded-full bg-radial-gradient from-[#2BC7B7]/20 to-transparent absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" initial={{
                      scale: 0,
                      opacity: 0
                    }} animate={{
                      scale: 1,
                      opacity: [0, 0.5, 0]
                    }} transition={{
                      duration: 2
                    }} data-id="element-495" data-mp="33j2fe2sslrc3" />
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>

              {/* FORM CONTENT (Relative z-10) */}
              <form onSubmit={handleSubmit} className="relative z-10 space-y-8" data-id="element-496" data-mp="2h9vm45te3p3n">
                {/* Field 1: Name */}
                <div className="relative group" data-id="element-497" data-mp="28ba6aifayo6k">
                  <label className="block text-xs font-medium text-[#F3F5F1]/70 uppercase tracking-[2px] mb-2 font-inter" data-id="element-498" data-mp="28b0ecpap66y8">
                    Your Name
                  </label>
                  <div className="relative" data-id="element-499" data-mp="1jroxw9r8pmv3">
                    <input type="text" value={form.name} onChange={e => setForm({
                    ...form,
                    name: e.target.value
                  })} onFocus={() => setFocused('name')} onBlur={() => setFocused(null)} placeholder="Enter your name" className={`w-full bg-[#1F3A33] border-2 rounded-xl px-5 py-4 text-[#F3F5F1] placeholder-[#F3F5F1]/40 outline-none transition-all duration-300
                        ${focused === 'name' ? 'border-[#2BC7B7] shadow-[0_0_20px_rgba(43,199,183,0.3)]' : 'border-transparent'}
                        ${isNameValid ? 'border-[#2BC7B7]/50' : ''}
                      `} data-id="element-500" data-mp="1jt04sifzfaqf" />
                    {isNameValid && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#2BC7B7]" data-id="element-501" data-mp="opogdpuexawn">
                        <Check className="w-5 h-5" data-id="element-502" data-mp="tgwrcqbe54bx" />
                      </div>}
                  </div>
                </div>

                {/* Field 2: Email */}
                <div className="relative group pt-12" data-id="element-503" data-mp="rz7iirj6zh2">
                  {' '}
                  {/* Extra padding for root space */}
                  <label className="block text-xs font-medium text-[#F3F5F1]/70 uppercase tracking-[2px] mb-2 font-inter" data-id="element-504" data-mp="5qajp05atm2g">
                    Email Address
                  </label>
                  <div className="relative" data-id="element-505" data-mp="es9zjp4kuk3t">
                    <input type="email" value={form.email} onChange={e => setForm({
                    ...form,
                    email: e.target.value
                  })} onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} placeholder="your@email.com" className={`w-full bg-[#1F3A33] border-2 rounded-xl px-5 py-4 text-[#F3F5F1] placeholder-[#F3F5F1]/40 outline-none transition-all duration-300
                        ${focused === 'email' ? 'border-[#2BC7B7] shadow-[0_0_20px_rgba(43,199,183,0.3)]' : 'border-transparent'}
                        ${isEmailValid ? 'border-[#2BC7B7]/50' : ''}
                        ${form.email.length > 0 && !isEmailValid && focused !== 'email' ? 'border-[#A38E78]' : ''}
                      `} data-id="element-506" data-mp="etbjkj3kmgxo" />
                    {isEmailValid && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#2BC7B7]" data-id="element-507" data-mp="31zxy4tq4zlv3">
                        <Check className="w-5 h-5" data-id="element-508" data-mp="2xyvw654u4vpf" />
                      </div>}
                    {form.email.length > 0 && !isEmailValid && focused !== 'email' && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A38E78]" data-id="element-509" data-mp="103pbhe0q351x">
                          <AlertCircle className="w-5 h-5" data-id="element-510" data-mp="mcam8pg827mf" />
                        </div>}
                  </div>
                </div>

                {/* Field 3: Message */}
                <div className="relative group pt-12" data-id="element-511" data-mp="1e3i2lag5vnaf">
                  <label className="block text-xs font-medium text-[#F3F5F1]/70 uppercase tracking-[2px] mb-2 font-inter" data-id="element-512" data-mp="1j1ji5ledp4h5">
                    Your Message
                  </label>
                  <div className="relative" data-id="element-513" data-mp="153uy2fs4y1d5">
                    <textarea value={form.message} onChange={e => setForm({
                    ...form,
                    message: e.target.value
                  })} onFocus={() => setFocused('message')} onBlur={() => setFocused(null)} placeholder="Tell us about your project, question, or how you'd like to collaborate..." rows={5} className={`w-full bg-[#1F3A33] border-2 rounded-xl px-5 py-4 text-[#F3F5F1] placeholder-[#F3F5F1]/40 outline-none transition-all duration-300 resize-y min-h-[160px]
                        ${focused === 'message' ? 'border-[#2BC7B7] shadow-[0_0_20px_rgba(43,199,183,0.3)]' : 'border-transparent'}
                        ${isMessageValid ? 'border-[#2BC7B7]/50' : ''}
                      `} data-id="element-514" data-mp="1a0256bw9piwo" />
                  </div>
                </div>

                {/* Convergence Zone Text */}
                <div className="h-24 flex items-center justify-center" data-id="element-515" data-mp="2kt2xst2khycd">
                  <motion.p className={`text-sm tracking-widest uppercase font-medium transition-colors duration-500 ${allValid ? 'text-[#4DE7D4]' : 'text-[#F3F5F1]/20'}`} animate={{
                  opacity: allValid ? 1 : 0.4
                }} data-id="element-516" data-mp="2kr8cveraq68a">
                    {allValid ? 'Ready to grow' : 'Roots seeking connection...'}
                  </motion.p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pb-4" data-id="element-517" data-mp="299ihzen5gpuj">
                  <button type="submit" disabled={!allValid || status !== 'idle'} className={`
                      relative px-12 py-4 rounded-full border-2 text-sm font-semibold tracking-[2px] uppercase transition-all duration-300
                      ${!allValid ? 'border-[#2BC7B7]/30 text-[#2BC7B7]/40 cursor-not-allowed' : 'border-[#2BC7B7] text-[#2BC7B7] hover:bg-[#2BC7B7] hover:text-[#0D2B24] hover:shadow-[0_0_30px_rgba(43,199,183,0.5)] hover:scale-105 active:scale-95'}
                      ${status === 'sending' ? 'bg-[#2BC7B7] text-[#0D2B24] shadow-[0_0_40px_rgba(43,199,183,0.6)]' : ''}
                    `} data-id="element-518" data-mp="2da2hupz3y7kd">
                    {status === 'sending' ? 'SENDING...' : 'SEND MESSAGE'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>

          {/* Right Column: Founder & Info */}
          <div className="lg:col-span-4 lg:col-start-11 xl:col-span-3 xl:col-start-10 space-y-8 lg:pt-20" data-id="element-519" data-mp="bdjuarn41z6q">
            {/* Founder Card */}
            <div className="hidden lg:block" data-id="element-520" data-mp="140o02yu12hjk">
              <FounderCard data-id="element-521" data-mp="141z48zrdxrb7" />
            </div>

            {/* Mobile/Tablet Founder Card (shown below form usually, but here handling responsive grid) */}
          </div>
        </div>

        {/* Bottom Section: Alternative Methods */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-522" data-mp="u90gfk3pq4sn">
          <ContactCard icon={Mail} title="Email Us Directly" value="natalya@vitalityvf.com" href="mailto:natalya@vitalityvf.com" data-id="element-523" data-mp="1rsblx2cfn2ez" />
          <ContactCard icon={Linkedin} title="Connect on LinkedIn" value="Vitality Venture Fund" href="https://www.linkedin.com/company/vitality-vf/" data-id="element-524" data-mp="2oonuig8v354a" />
          <ContactCard icon={MapPin} title="Based In" value="United States" href={null} data-id="element-525" data-mp="2xmzfo2qylapc" />
        </div>

        {/* Mobile Founder Card (visible only on small screens) */}
        <div className="mt-12 lg:hidden flex justify-center" data-id="element-526" data-mp="32of13vx1tj89">
          <FounderCard data-id="element-527" data-mp="32nx0kn3csiuj" />
        </div>
      </main>
    </div>;
}
// --- Sub-component: Root System Visualizer ---
const RootSystem = ({
  isActive,
  isValid,
  length,
  seed,
  color,
  opacity
}: {
  isActive: boolean;
  isValid: boolean;
  length: number;
  seed: number;
  color: string;
  opacity: number;
}) => {
  // Generate main path
  const mainPath = useMemo(() => generateWiggle(0, 0, 300, seed), [seed]);
  // Generate branches based on length
  const branches = useMemo(() => {
    const count = Math.floor(length / 50);
    return generateBranches(mainPath, count, seed);
  }, [length, seed, mainPath]);
  return <g style={{
    opacity,
    transition: 'opacity 0.5s ease'
  }} data-id="element-528" data-mp="3v2aitb2d0iqv">
      {/* Glow Filter */}
      <defs data-id="element-529" data-mp="3vg3c86wrbq9">
        <filter id={`glow-${seed}`} data-id="element-530" data-mp="3v6amcuhaqgv">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" data-id="element-531" data-mp="3u4s5nb4v1p4" />
          <feMerge data-id="element-532" data-mp="3m8xg5hehu2rl">
            <feMergeNode in="coloredBlur" data-id="element-533" data-mp="3m8e0y8g89sh9" />
            <feMergeNode in="SourceGraphic" data-id="element-534" data-mp="3r0f6zcmsymyu" />
          </feMerge>
        </filter>
      </defs>

      {/* Main Root */}
      <motion.path d={mainPath} stroke={color} strokeWidth={isValid ? 3 : 2} fill="none" strokeLinecap="round" filter={`url(#glow-${seed})`} initial={{
      pathLength: 0
    }} animate={{
      pathLength: isActive ? length / 300 : 0
    }} // Normalize length to path
    transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} data-id="element-535" data-mp="3cckcbqp8i8j4" />

      {/* Branches */}
      {branches.map((branch, i) => <motion.path key={branch.id} d={branch.d} stroke={color} strokeWidth={1.5} fill="none" strokeLinecap="round" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 0.8,
      delay: 0.2 + i * 0.1
    }} transform={`translate(0, ${branch.yOffset})`} // Simplified positioning
    data-id="element-536" data-mp="1927m9egwggkd" data-mpi={i} />)}

      {/* Pulse Effect for Valid Roots */}
      {isValid && <motion.circle r="2" fill="#fff" initial={{
      offsetDistance: '0%'
    }} animate={{
      offsetDistance: '100%'
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'linear',
      repeatDelay: 1
    }} style={{
      offsetPath: `path("${mainPath}")`
    }} data-id="element-537" data-mp="1it27qgmbnc4r" />}
    </g>;
};